import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Plus, Minus, Loader2 } from "lucide-react";

function useFAQs() {
  return useQuery({
    queryKey: ["faqs"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/faqs");
      return data.faqs || data || [];
    },
    staleTime: 10 * 60_000,
  });
}

export default function FAQPage() {
  const q = useFAQs();
  const items = q.data || [];
  const [open, setOpen] = useState(null);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="FAQ" description="Frequently asked questions about Allsale Classified — buying, selling, payments and safety." />
      <Breadcrumbs items={[{ label: "FAQ" }]} />
      <h1 className="mt-6 font-display text-4xl font-black text-slate-900">Frequently asked questions</h1>
      <p className="mt-2 text-slate-500">Everything you need to know to buy and sell on Allsale.</p>

      {q.isLoading && <div className="mt-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

      <ul className="mt-10 space-y-3">
        {items.map((f, i) => {
          const isOpen = open === i;
          return (
            <li key={f.id || i} className="rounded-2xl bg-white shadow-card overflow-hidden" data-testid={`faq-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 text-left"
              >
                <span className="font-display font-bold text-slate-900">{f.question}</span>
                <span className="shrink-0 w-8 h-8 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
                  {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </span>
              </button>
              {isOpen && (
                <div className="px-5 md:px-6 pb-5 text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {f.answer}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
